import React, { useEffect, useState } from "react";
import { ErrorMessage, Form, Formik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import {
  FiCheck,
  FiLock,
  FiMapPin,
  FiUser,
} from "react-icons/fi";
import InputField from "components/forms/formik-field/InputField";
import { useLocationContext } from "hooks‬/LocationContext";
import apiInterceptor from "services/apiInterceptor";
import { useParams } from "react-router-dom";
import { subscriptionApiEndpoint } from "constant/apiConstants";
import profileSetupComplete from "assets/svg/profileSetupComplete.svg";
import { ERROR_STATUS_KEY } from "constant/errorCode";
import { useAuthValidationRules } from "helpers/validation-schema/AuthValidationRules";
import { relNoopener, targetblank } from "constant/commonConstant";
import { InvelidLink } from "components/error-pages/InvelidLink";
import PageLoader from "components/base/PageLoader";

const STEPS = [
  { step: 1, label: "Personal Details", icon: FiUser },
  { step: 2, label: "Contact Details", icon: FiMapPin },
  { step: 3, label: "Set Your Password", icon: FiLock },
];

const UserInvitationOnboardingForm = () => {
  const [step, setStep] = useState(1);
  const [countdown, setCountdown] = useState(5);

  const validationRules = useAuthValidationRules();

  const { guid } = useParams();
  const {
    fetchCountries,
    countries,
    fetchStates,
    states,
    fetchCities,
    cities,
    setStates,
    setCities,
  } = useLocationContext();
  const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseError, setResponseError] = useState("");
  const [isLinkInvalid, setIsLinkInvalid] = useState(false);
  const [linkValidationError, setLinkValidationError] = useState("");
  const [pageLoader, setPageLoader] = useState(true);
  useEffect(() => {
    const urlChecker = () => {
      setPageLoader(true);
      apiInterceptor
        .get(`${subscriptionApiEndpoint.USERS_ONBOARDING}/${guid}`)
        .then((response) => {
          if (response.data.status === ERROR_STATUS_KEY) {
            setLinkValidationError(response.data.statusDesc);
            setIsLinkInvalid(true);
          } else {
            setIsLinkInvalid(false);
          }
          setPageLoader(false);
        })
        .catch((error) => {
          setPageLoader(false);
          setIsLinkInvalid(true);
          setLinkValidationError(
            "An error occurred while checking the onboarding status."
          );
        });
    };
    urlChecker();
    fetchCountries();

    return () => {
      setStates([]);
      setCities([]);
    };
    // eslint-disable-next-line
  }, []);

  // Add countdown effect for redirect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (submittedSuccessfully && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (submittedSuccessfully && countdown === 0) {
      // Redirect to login page
      window.location.href = "/login";
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [submittedSuccessfully, countdown]);

  const validationSchemas = [
    Yup.object({
      firstName: validationRules.firstNameValidationSchema,
      lastName: validationRules.lastNameValidationSchema,
      mobileNumber: validationRules.contactNumberValidationSchema,
    }),
    Yup.object({
      address1: validationRules.address1ValidationSchema,
      address2: Yup.string(),
      pinCode: Yup.string().required("Pin code is required"),
      country: Yup.object().nullable().required("Country is required"),
      state: Yup.object().nullable().required("State is required"),
      city: Yup.object().nullable().required("City is required"),
    }),
    Yup.object({
      password: validationRules.passwordValidationSchema,
      confirmPassword: validationRules.confirmPasswordValidationSchema,
    }),
  ];

  const initialValues = {
    firstName: "",
    lastName: "",
    mobileNumber: "",
    address1: "",
    address2: "",
    status: "",
    pinCode: "",
    country: null,
    state: null,
    city: null,
    password: "",
    confirmPassword: "",
  };

  const handleSubmit = async (values: any, actions: any) => {
    actions.setErrors({});
    actions.setTouched({});

    if (step < 3) return setStep(step + 1);
    setResponseError("");
    const payload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      mobileNumber: values.mobileNumber.trim(),
      address1: values.address1.trim(),
      address2: values.address2.trim(),
      pinCode: values.pinCode.trim(),
      cityGuid: values.city?.value ?? "",
      password: values.password.trim(),
    };
    setIsSubmitting(true);
    setSubmittedSuccessfully(false);
    await apiInterceptor
      .put(
        `${subscriptionApiEndpoint.USERS_ONBOARDING}/${guid}`,
        payload
      )
      .then((response) => {
        if (response.data.status === ERROR_STATUS_KEY) {
          setResponseError(response.data.statusDesc);
        } else {
          setSubmittedSuccessfully(true);
          setIsSubmitting(false);
          setCountdown(5);
        }
      })
      .catch((error) => {
        console.error("Error inviting user:", error);
        setIsSubmitting(false);
      });
  };

  if (pageLoader) {
    return <PageLoader />;
  }
  if (isLinkInvalid) {
    return <InvelidLink linkValidationError={linkValidationError} />;
  }

  return (
    <div className="min-vh-100 d-flex flex-column" style={{ backgroundColor: "#f5f6fa" }}>
      <div className="d-flex align-items-center justify-content-between bg-white px-10 py-5 border-bottom">
        <img src="/NBlight.svg" alt="Logo" className="h-35px" />
        <a
          rel={relNoopener}
          href="mailto:support@niyantrasbizro.com"
          target={targetblank}
          className="text-gray-700 fw-semibold fs-6"
        >
          Need help?
        </a>
      </div>

      {submittedSuccessfully ? (
        <div className="flex-grow-1 d-flex align-items-center justify-content-center px-5 py-15">
          <div
            className="card shadow-sm rounded-4 text-center p-10 p-md-15"
            style={{ maxWidth: 520, width: "100%" }}
          >
            <img
              src={profileSetupComplete}
              alt="Setup complete"
              className="mh-200px m-auto mb-6"
            />
            <div className="fs-2 fw-bold text-gray-900 mb-2">
              Profile Setup Complete
            </div>
            <div className="text-gray-600 fs-5">
              Your account has been successfully set up. You&apos;ll be
              redirected to the login page in {countdown} second
              {countdown !== 1 ? "s" : ""}.
            </div>
            <button
              className="btn btn-primary mt-8"
              onClick={() => {
                window.location.href = "/login";
              }}
            >
              Proceed to Login Now
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-grow-1 d-flex justify-content-center px-5 py-10">
          <div style={{ maxWidth: 680, width: "100%" }}>
            <div className="d-flex align-items-start justify-content-between mb-10 px-2">
              {STEPS.map((s, index) => {
                const isDone = step > s.step;
                const isActive = step === s.step;
                const Icon = s.icon;
                return (
                  <React.Fragment key={s.step}>
                    <div className="d-flex flex-column align-items-center" style={{ minWidth: 90 }}>
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: 40,
                          height: 40,
                          backgroundColor: isDone || isActive ? "var(--bs-primary)" : "#e4e6ef",
                          color: isDone || isActive ? "#fff" : "#7e8299",
                          transition: "background-color .2s ease",
                        }}
                      >
                        {isDone ? <FiCheck size={18} /> : <Icon size={17} />}
                      </div>
                      <div
                        className={`fs-8 fw-semibold text-center mt-2 ${
                          isActive ? "text-primary" : isDone ? "text-gray-800" : "text-gray-500"
                        }`}
                      >
                        {s.label}
                      </div>
                    </div>
                    {index < STEPS.length - 1 && (
                      <div
                        className="flex-grow-1 mt-5"
                        style={{
                          height: 2,
                          backgroundColor: step > s.step ? "var(--bs-primary)" : "#e4e6ef",
                          transition: "background-color .2s ease",
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {responseError.length > 0 && (
              <div className="alert alert-danger text-center mb-6">
                {responseError}
              </div>
            )}

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchemas[step - 1]}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue }) => (
                <Form placeholder={"Enter your details"}>
                  <div className="card shadow-sm rounded-4">
                    <div className="card-body p-8 p-md-10">
                      {step === 1 && (
                        <>
                          <div className="fs-3 fw-bold text-gray-900 mb-1">
                            Tell us about yourself
                          </div>
                          <div className="text-gray-600 fs-6 mb-8">
                            Enter your name and basic personal details to help
                            us build your profile.
                          </div>

                          <div className="row">
                            <div className="col-md-6 mb-6">
                              <InputField
                                name="firstName"
                                type="text"
                                label="First Name"
                                required
                              />
                            </div>
                            <div className="col-md-6 mb-6">
                              <InputField
                                name="lastName"
                                type="text"
                                label="Last Name"
                                required
                              />
                            </div>
                          </div>
                          <InputField
                            name="mobileNumber"
                            type="text"
                            label="Mobile Number"
                            required
                          />
                        </>
                      )}

                      {step === 2 && (
                        <>
                          <div className="fs-3 fw-bold text-gray-900 mb-1">
                            How can we reach you?
                          </div>
                          <div className="text-gray-600 fs-6 mb-8">
                            Provide your contact information so we can stay
                            in touch and keep you updated.
                          </div>

                          <div className="mb-6">
                            <InputField
                              name="address1"
                              type="text"
                              label="Address 1"
                              required
                            />
                          </div>
                          <div className="mb-6">
                            <InputField
                              name="address2"
                              type="text"
                              label="Address 2"
                            />
                          </div>

                          <div className="row">
                            <div className="col-md-4 mb-6">
                              <label className="fs-6 form-label required">
                                Country
                              </label>
                              <Select
                                options={countries}
                                value={values.country}
                                onChange={(option) => {
                                  setFieldValue("country", option);
                                  fetchStates(option?.value ?? "");
                                }}
                                className="react-select-styled react-select-lg border-select"
                                classNamePrefix="react-select"
                              />
                              <div className="text-danger fs-8 mt-1">
                                <ErrorMessage name="country" />
                              </div>
                            </div>

                            <div className="col-md-4 mb-6">
                              <label className="fs-6 form-label required">
                                State
                              </label>
                              <Select
                                options={states}
                                value={values.state}
                                onChange={(option) => {
                                  setFieldValue("state", option);
                                  fetchCities(option?.value ?? "");
                                }}
                                className="react-select-styled react-select-lg border-select"
                                classNamePrefix="react-select"
                              />
                              <div className="text-danger fs-8 mt-1">
                                <ErrorMessage name="state" />
                              </div>
                            </div>

                            <div className="col-md-4 mb-6">
                              <label className="fs-6 form-label required">
                                City
                              </label>
                              <Select
                                options={cities}
                                value={values.city}
                                onChange={(option) => setFieldValue("city", option)}
                                className="react-select-styled react-select-lg border-select"
                                classNamePrefix="react-select"
                              />
                              <div className="text-danger fs-8 mt-1">
                                <ErrorMessage name="city" />
                              </div>
                            </div>
                          </div>

                          <InputField
                            name="pinCode"
                            type="text"
                            label="Pin Code"
                            required
                          />
                        </>
                      )}

                      {step === 3 && (
                        <>
                          <div className="fs-3 fw-bold text-gray-900 mb-1">
                            Secure your account
                          </div>
                          <div className="text-gray-600 fs-6 mb-8">
                            Create a strong password to keep your account
                            safe and accessible.
                          </div>

                          <div className="mb-6">
                            <InputField
                              name="password"
                              showPasswordToggle={true}
                              type="password"
                              label="Password"
                              required
                            />
                          </div>
                          <InputField
                            name="confirmPassword"
                            showPasswordToggle={true}
                            type="password"
                            label="Confirm Password"
                            required
                          />
                        </>
                      )}
                    </div>

                    <div className="card-footer bg-transparent border-top d-flex justify-content-end gap-3 py-5 px-8 px-md-10">
                      {step > 1 && (
                        <button
                          className="btn btn-light"
                          type="button"
                          onClick={() => setStep(step - 1)}
                        >
                          Back
                        </button>
                      )}
                      <button
                        disabled={isSubmitting}
                        type="submit"
                        className="btn btn-primary"
                      >
                        {step === 3 ? "Submit" : "Save & Continue"}
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserInvitationOnboardingForm;
